import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { useOnBoardingContext } from "../OnBoardingContext";
import { GfStoreDto } from "@swagger/typescript-fetch-goflyer";
import { GooglePlaceResult } from "../useStoreSearch";
import { useGoogleMapSearchContext } from "./GoogleMapSearchContext";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";

export const SearchResults: React.FC = () => {
  const {
    results,
    googleResults,
    loading,
    error,
    googleLoading,
    googleError,
    handleSelect,
    hasSearched,
  } = useGoogleMapSearchContext();
  const { selectedStore } = useOnBoardingContext();
  const { t } = useTranslation();

  const renderLoadingState = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: 120,
      }}
    >
      <CircularProgress size={28} sx={{ color: "#f58321" }} />
    </Box>
  );

  const renderEmptyState = () => (
    <Box
      sx={{
        color: "text.secondary",
        p: 3,
        textAlign: "center",
        fontSize: 16,
      }}
    >
      <Typography variant="body1" gutterBottom>
        {t(messages.No_stores_found_Try_a_different_search())}
      </Typography>
      {hasSearched && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t(
            messages.Cant_find_your_store_You_can_proceed_to_enter_your_store_details_manually()
          )}
        </Typography>
      )}
    </Box>
  );

  const renderSearchResults = () => (
    <List disablePadding aria-label="Store search results">
      {results.map((item, idx) => (
        <React.Fragment
          key={
            item.source === "api"
              ? (item.data as GfStoreDto).id
              : (item.data as GooglePlaceResult).place_id
          }
        >
          <ListItem
            button
            selected={selectedStore?.store === item.data}
            onClick={() => handleSelect(item)}
            sx={{
              cursor: "pointer",
              "&.Mui-selected": {
                backgroundColor: "rgba(245, 131, 33, 0.1)",
                borderLeft: "3px solid #f58321",
              },
              "&:hover": { backgroundColor: "rgba(2, 183, 207, 0.05)" },
              transition: "background 0.2s",
            }}
            aria-selected={selectedStore?.store === item.data}
            tabIndex={0}
          >
            <ListItemText
              primary={
                item.source === "api"
                  ? (item.data as GfStoreDto).address ||
                    (item.data as GfStoreDto).signature
                  : (item.data as GooglePlaceResult).name
              }
              secondary={
                item.source === "api"
                  ? (item.data as GfStoreDto).city
                  : (item.data as GooglePlaceResult).formatted_address
              }
              primaryTypographyProps={{ fontWeight: 500, fontSize: 16 }}
              secondaryTypographyProps={{
                fontSize: 14,
                color: "text.secondary",
              }}
            />
          </ListItem>
          {idx < results.length - 1 && (
            <Box
              component="li"
              sx={{ borderBottom: "1px solid #eee", mx: 2 }}
            />
          )}
        </React.Fragment>
      ))}

      {results.length === 0 &&
        googleResults.map((place, idx) => (
          <React.Fragment key={place.place_id}>
            <ListItem
              button
              selected={
                selectedStore?.store &&
                (selectedStore.store as GooglePlaceResult).place_id ===
                  place.place_id
              }
              onClick={() => handleSelect({ source: "google", data: place })}
              sx={{
                cursor: "pointer",
                "&.Mui-selected": {
                  backgroundColor: "rgba(245, 131, 33, 0.1)",
                  borderLeft: "3px solid #f58321",
                },
                "&:hover": { backgroundColor: "rgba(2, 183, 207, 0.05)" },
                transition: "background 0.2s",
              }}
              aria-selected={
                selectedStore?.store &&
                (selectedStore.store as GooglePlaceResult).place_id ===
                  place.place_id
              }
              tabIndex={0}
            >
              <ListItemText
                primary={place.name}
                secondary={place.formatted_address}
                primaryTypographyProps={{ fontWeight: 500, fontSize: 16 }}
                secondaryTypographyProps={{
                  fontSize: 14,
                  color: "text.secondary",
                }}
              />
            </ListItem>
            {idx < googleResults.length - 1 && (
              <Box
                component="li"
                sx={{ borderBottom: "1px solid #eee", mx: 2 }}
              />
            )}
          </React.Fragment>
        ))}
    </List>
  );

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box
        component="h3"
        sx={{ fontWeight: 500, fontSize: 18, mb: 1, color: "#f58321" }}
      >
        {t(messages.Search_Results())}
      </Box>

      <Paper
        variant="outlined"
        sx={{ minHeight: 220, maxHeight: 320, overflow: "auto", p: 0 }}
      >
        {loading && renderLoadingState()}
        {error && (
          <Box sx={{ color: "error.main", p: 2, textAlign: "center" }}>
            {error}
          </Box>
        )}
        {!loading &&
          !error &&
          results.length === 0 &&
          googleResults.length === 0 &&
          !googleLoading &&
          renderEmptyState()}

        {renderSearchResults()}

        {googleLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 80,
            }}
          >
            <CircularProgress size={24} sx={{ color: "#f58321" }} />
          </Box>
        )}
        {googleError && (
          <Box sx={{ color: "error.main", p: 2, textAlign: "center" }}>
            {googleError}
          </Box>
        )}
      </Paper>
    </Box>
  );
};
